<!--
  EhrPatientSelectInput.vue
  One field for naming a patient. Typing a name searches the EHR through the
  DQMP bridge and shows matches in a teleported dropdown; picking one collapses
  it into an inline chip. Results overlay the page rather than sitting in normal
  flow, so nothing below the field moves while you type.

  mode="name"    (default) a typed name is itself a valid answer - AI Scribe
  mode="contact" a typed phone or email is the answer instead; phones are masked
                 as you type and, outside the EHR, the dropdown says so
-->
<template>
  <div class="ehr-select">
    <label v-if="label" class="ehr-select-label">{{ label }}<span v-if="required" class="ehr-select-required">*</span></label>
    <div ref="fieldWrapper">
      <div class="ehr-select-field" :class="{ 'ehr-select-field--selected': selectedPatient, 'ehr-select-field--disabled': disabled }"
        @click="focusInput">
        <template v-if="selectedPatient">
          <img :src="avatarFor(selectedPatient)" alt="Patient" class="ehr-select-avatar">
          <span class="ehr-select-name">{{ selectedPatientName }}</span>
          <button type="button" class="ehr-select-clear" @click.stop="clearSelection">
            <vue-feather type="x" size="16" />
          </button>
        </template>
        <input v-else ref="input" type="text" class="ehr-select-input" :placeholder="resolvedPlaceholder"
          :value="query" :inputmode="inputMode" :disabled="disabled" autocomplete="nope" data-lpignore="true"
          data-1p-ignore="true" data-form-type="other" @input="handleInput" @focus="handleFocus" @blur="handleBlur" />
      </div>
    </div>

    <FloatingDropdown :open="showDropdown" :trigger-ref="$refs.fieldWrapper" @dismiss="dropdownDismissed = true">
      <div class="ehr-select-dropdown">
        <div class="ehr-select-dropdown-list">
          <div v-if="!isEmbedded" class="ehr-select-dropdown-message">
            Name search is available when embedded in your EHR
          </div>
          <div v-else-if="searchError" class="ehr-select-dropdown-message ehr-select-dropdown-error">
            {{ searchError }}
          </div>
          <div v-else-if="noResults" class="ehr-select-dropdown-message">
            No patients found
          </div>
          <div v-for="patient of patients" :key="patient.emrIntegrationId" class="ehr-select-card"
            @mousedown.prevent="selectPatient(patient)">
            <div class="ehr-select-card-avatar">
              <img :src="avatarFor(patient)" alt="Patient">
            </div>
            <div class="ehr-select-card-info">
              <span class="ehr-select-card-name">{{ patient.firstName }} {{ patient.lastName }}</span>
              <span class="ehr-select-card-meta">
                <template v-if="patient.emrRecordNumber">ID {{ patient.emrRecordNumber }} · </template>
                <template v-if="patient.dateOfBirth">DOB {{ patient.dateOfBirth }}</template>
              </span>
            </div>
            <vue-feather type="chevron-right" size="16" class="ehr-select-card-arrow" />
          </div>
        </div>
      </div>
    </FloatingDropdown>
  </div>
</template>

<script>
import FloatingDropdown from '../floating/FloatingDropdown.vue'
import { send } from '@/iframe'
import VueFeather from 'vue-feather'

const PLACEHOLDERS = {
  name: 'Search or type a patient name...',
  contact: 'Phone, email, or patient name...'
};

export default {
  name: 'EhrPatientSelectInput',
  components: { FloatingDropdown, 'vue-feather': VueFeather },
  props: {
    // The selected patient, normalized (see normalizePatient). null when none.
    modelValue: { type: Object, default: null },
    // Raw text in the field, for parents that treat typed input as an answer
    query: { type: String, default: '' },
    label: { type: String, default: 'Patient' },
    placeholder: { type: String, default: '' },
    mode: {
      type: String,
      default: 'name',
      validator: (value) => ['name', 'contact'].includes(value)
    },
    prefill: { type: Object, default: null },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'update:query', 'select', 'clear'],
  data() {
    return {
      isEmbedded: window.parent !== window,
      isFocused: false,
      dropdownDismissed: false,
      patients: [],
      noResults: false,
      searchError: '',
      searchTimeout: null
    };
  },
  computed: {
    selectedPatient() {
      return this.modelValue;
    },
    selectedPatientName() {
      if (!this.selectedPatient) return '';
      return [this.selectedPatient.firstName, this.selectedPatient.lastName].filter(Boolean).join(' ');
    },
    resolvedPlaceholder() {
      return this.placeholder || PLACEHOLDERS[this.mode];
    },
    detectedType() {
      if (this.mode !== 'contact') return 'name';
      return this.detectInputType(this.query);
    },
    inputMode() {
      if (this.detectedType === 'phone') return 'numeric';
      if (this.detectedType === 'email') return 'email';
      return 'text';
    },
    showDropdown() {
      if (this.dropdownDismissed || this.selectedPatient) return false;
      if (this.detectedType !== 'name') return false;
      if (this.isEmbedded) return this.patients.length > 0 || this.noResults || !!this.searchError;
      // Outside the EHR a typed name is a perfectly good answer in name mode, so
      // only contact mode needs to explain why nothing is being searched.
      return this.mode === 'contact' && this.query.length >= 3;
    }
  },
  watch: {
    query(value) {
      this.searchError = '';
      this.dropdownDismissed = false;

      // Search runs through the extension bridge only. Outside the iframe send()
      // has no listener and rejects after its 120s timeout. Whitespace alone would
      // reach the EHR as an empty search, and a pending search must not fire once
      // the box no longer qualifies (e.g. cleared).
      const queryString = value.trim();
      clearTimeout(this.searchTimeout);
      if (this.detectedType === 'name' && this.isEmbedded && queryString.length >= 3) {
        this.searchTimeout = setTimeout(() => this.searchPatients(queryString), 400);
      } else {
        this.patients = [];
        this.noResults = false;
      }
    },
    prefill: {
      immediate: true,
      handler(value) {
        if (value && !this.modelValue) this.selectPatient(value, { silent: true });
      }
    }
  },
  beforeUnmount() {
    clearTimeout(this.searchTimeout);
  },
  methods: {
    detectInputType(value) {
      if (!value || value.trim().length === 0) return 'name';
      const trimmed = value.trim();
      if (trimmed.includes('@')) return 'email';
      const digits = trimmed.replace(/\D/g, '');
      if (digits.length > 0 && digits.length === trimmed.replace(/[\s()-]/g, '').length) {
        return 'phone';
      }
      return 'name';
    },

    handleInput(event) {
      const newValue = event.target.value;

      if (this.mode !== 'contact' || this.detectInputType(newValue) !== 'phone') {
        this.$emit('update:query', newValue);
        return;
      }

      const input = event.target;
      const cursorPos = input.selectionStart;
      const prevLen = newValue.length;
      const digits = newValue.replace(/\D/g, '').slice(0, 10);

      let formatted = '';
      if (digits.length > 6) {
        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else if (digits.length > 0) {
        formatted = `(${digits}`;
      }

      this.$emit('update:query', formatted);

      // Vue skips the re-render when the formatted value is unchanged (e.g. capped
      // at 10 digits), so push it onto the element before restoring the cursor.
      this.$nextTick(() => {
        input.value = formatted;
        const newCursor = Math.max(0, cursorPos + (formatted.length - prevLen));
        input.setSelectionRange(newCursor, newCursor);
      });
    },

    handleFocus() {
      this.isFocused = true;
      this.dropdownDismissed = false;
    },

    handleBlur() {
      this.isFocused = false;
      setTimeout(() => {
        if (!this.isFocused) this.dropdownDismissed = true;
      }, 150);
    },

    focusInput() {
      if (this.disabled) return;
      this.$refs.input?.focus();
    },

    avatarFor(patient) {
      if (patient.avatar) return patient.avatar;
      if (!patient.pictureBase64) return '/icons/patient.png';
      if (patient.pictureBase64.startsWith('data:')) return patient.pictureBase64;
      return `data:image/png;base64,${patient.pictureBase64}`;
    },

    normalizePatient(patient) {
      return {
        firstName: patient.firstName || '',
        lastName: patient.lastName || '',
        dateOfBirth: patient.dateOfBirth || '',
        emrIntegrationId: patient.emrIntegrationId || '',
        emrRecordNumber: patient.emrRecordNumber || '',
        avatar: this.avatarFor(patient),
        // Contact fields ride along when the search hit already carries them
        mobilePhone: patient.mobilePhone || '',
        homePhone: patient.homePhone || '',
        workPhone: patient.workPhone || '',
        emailAddress: patient.emailAddress || ''
      };
    },

    async searchPatients(queryString) {
      try {
        this.noResults = false;
        this.searchError = '';
        // A leading digit means they are typing a date of birth
        const searchType = /^\d/.test(queryString) ? 'dob' : 'name';
        const result = await send('DQMP_SEARCH_PATIENTS', { queryString, searchType });
        // The box changed while the EHR was answering — drop the stale results
        if (queryString !== this.query.trim()) return;
        this.patients = result?.patientList || [];
        this.noResults = this.patients.length === 0;
      } catch (err) {
        if (queryString !== this.query.trim()) return;
        console.error('EHR patient search failed', err);
        this.patients = [];
        this.searchError = err.message || 'Search failed';
      }
    },

    selectPatient(patient, { silent = false } = {}) {
      const normalized = this.normalizePatient(patient);
      this.patients = [];
      this.noResults = false;
      this.$emit('update:modelValue', normalized);
      this.$emit('update:query', '');
      if (!silent) this.$emit('select', normalized);
    },

    clearSelection() {
      this.$emit('update:modelValue', null);
      this.$emit('update:query', '');
      this.$emit('clear');
      this.$nextTick(() => this.$refs.input?.focus());
    }
  }
};
</script>

<style scoped>
.ehr-select {
  display: flex;
  flex-direction: column;
}

.ehr-select-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 6px;
}

.ehr-select-required {
  color: #dc2626;
  margin-left: 2px;
}

.ehr-select-field {
  display: flex;
  align-items: center;
  height: 45px;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  transition: all 0.15s ease;
  cursor: text;
}

.ehr-select-field:hover:not(.ehr-select-field--selected):not(.ehr-select-field--disabled) {
  border-color: #9ca3af;
}

.ehr-select-field:focus-within:not(.ehr-select-field--selected) {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.ehr-select-field--selected {
  cursor: pointer;
}

.ehr-select-field--selected:hover {
  background: #f9fafb;
}

.ehr-select-field--disabled {
  background: #fafafa;
  cursor: not-allowed;
}

.ehr-select-input {
  width: 100%;
  height: 45px;
  padding: 0 14px;
  font-size: 1rem;
  font-weight: 400;
  color: #374151;
  background: transparent;
  border: none;
  outline: none;
  border-radius: 4px;
}

.ehr-select-input::placeholder {
  color: #9ca3af;
}

.ehr-select-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  margin-left: 10px;
}

.ehr-select-name {
  flex: 1;
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-left: 10px;
}

.ehr-select-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 4px;
  margin-right: 6px;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.ehr-select-clear:hover {
  background: #f3f4f6;
  color: #374151;
}
</style>

<style>
/* Dropdown styles are not scoped so they survive the teleport to <body>.
   Prefixed because StartSubmissionModal ships its own global .ehr-dropdown. */
.ehr-select-dropdown {
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.ehr-select-dropdown .ehr-select-dropdown-list {
  max-height: min(var(--fd-max-height, 9999px), 320px);
  overflow-y: auto;
}

.ehr-select-dropdown .ehr-select-card {
  padding: 12px;
  display: flex;
  gap: 12px;
  align-items: center;
  cursor: pointer;
  border-radius: 4px;
}

.ehr-select-dropdown .ehr-select-card:hover {
  background-color: #f1f5f9;
}

.ehr-select-dropdown .ehr-select-card:hover .ehr-select-card-arrow {
  color: var(--color-primary, #683fff);
}

.ehr-select-dropdown .ehr-select-card-avatar {
  flex-shrink: 0;
}

.ehr-select-dropdown .ehr-select-card-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.ehr-select-dropdown .ehr-select-card-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ehr-select-dropdown .ehr-select-card-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1e293b;
}

.ehr-select-dropdown .ehr-select-card-meta {
  font-size: 0.875rem;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}

.ehr-select-dropdown .ehr-select-card-arrow {
  color: #cbd5e1;
  flex-shrink: 0;
}

.ehr-select-dropdown .ehr-select-dropdown-message {
  padding: 12px;
  font-size: 0.8125rem;
  color: #6b7280;
  font-style: italic;
}

.ehr-select-dropdown .ehr-select-dropdown-error {
  color: #dc2626;
}
</style>
